import { Flex, Form, Radio } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const DEFAULT_QUESTIONS = [
    "The overall quality of the products",
    "The quality of packaging",
    "The quality of store's presentation",
    "The quality of customer service",
];

const DEFAULT_OPTIONS = ["Very Poor", "Poor", "Neutral", "Good", "Excellent"];

const buildItems = (items, count, defaults, prefix, withScore = false) => {
    const safeCount = Math.min(Math.max(Number(count) || defaults.length, 1), 10);
    return Array.from({ length: safeCount }, (_, index) => {
        const existing = items?.[index] || {};
        return {
            ...existing,
            label: existing.label || defaults[index] || `${prefix} ${index + 1}`,
            value: existing.value || `${prefix.toLowerCase().replaceAll(" ", "-")}-${index + 1}`,
            ...(withScore ? { score: existing.score ?? index + 1 } : {}),
        };
    });
};

const RatingScaleMatrix = ({ value = {}, onChange, questions, options, disabled }) => {
    const currentValue = value && typeof value === "object" && !Array.isArray(value) ? value : {};

    const handleChange = (questionValue, optionValue) => {
        onChange?.({
            ...currentValue,
            [questionValue]: optionValue,
        });
    };

    return (
        <div className="rating-scale-field">
            <div className="rating-scale-grid" style={{ gridTemplateColumns: `minmax(150px, 1.4fr) repeat(${options.length}, minmax(82px, 1fr))` }}>
                <div className="rating-scale-corner" />
                {options.map((option) => (
                    <div className="rating-scale-option-label" key={option.value}>
                        {option.label}
                    </div>
                ))}
                {questions.map((question) => (
                    <div className="rating-scale-row" key={question.value}>
                        <div className="rating-scale-question-label">{question.label}</div>
                        {options.map((option) => (
                            <Radio
                                key={option.value}
                                checked={currentValue?.[question.value] === option.value}
                                disabled={disabled}
                                onChange={() => handleChange(question.value, option.value)}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const RatingScaleInput = (props) => {
    const {
        label,
        tooltip,
        helpText,
        mandatory,
        readOnly,
        previewMode = false,
        id,
        questions = [],
        options = [],
        questionCount,
        optionCount,
    } = props;

    const matrixQuestions = buildItems(questions, questionCount, DEFAULT_QUESTIONS, "Question");
    const matrixOptions = buildItems(options, optionCount, DEFAULT_OPTIONS, "Option", true);
    const disabled = previewMode && readOnly;

    const help = tooltip
        ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;

    const rules = mandatory
        ? [
            {
                validator: (_, value) => {
                    const currentValue = value && typeof value === "object" && !Array.isArray(value) ? value : {};
                    const hasEveryAnswer = matrixQuestions.every((question) => currentValue?.[question.value]);
                    if (hasEveryAnswer) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(`${label || "Rating Scale"} is required`));
                },
            },
        ]
        : undefined;

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item name={id} label={label} tooltip={help} rules={rules} className="rating-scale-form-item">
                    <RatingScaleMatrix questions={matrixQuestions} options={matrixOptions} disabled={disabled} />
                </Form.Item>
            </Flex>
        </div>
    );
};

export default RatingScaleInput;
